import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { 
  Card, 
  CardContent,
  CardMedia,  
  Typography, 
  IconButton, 
  CircularProgress,
  Box,
  Chip,
  Tooltip,
} from '@mui/material';
import {
  ArrowDownTrayIcon,
  DocumentIcon,
  PhotoIcon,
  TrashIcon,
  EyeIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import {
  DocumentTextIcon,
  TableCellsIcon,
  DocumentChartBarIcon
} from '@heroicons/react/24/outline';
import { deleteAttachment } from "../../features/attachment/attachmentsSlice";
import { fetchAttachmentDownloadUrl } from '../../utils/api';
import ConfirmationModal from '../Modal/ConfirmationModal';
import { getDeleteModalTitle, getDeleteConfirmMessage } from '../../utils/uiText';
import ImagePreviewModal from '../Modal/ImagePreviewModal';
import { useScanningStatusSSE } from '../../hooks/useScanningStatusSSE';
import { CheckCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';

const AttachmentCard = ({ 
  attachment, 
  canEdit = false, 
  onDownload, 
  customActions = [],
  onDelete, // New prop for custom delete handling
}) => {
  const dispatch = useDispatch();
  const [downloadUrl, setDownloadUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);

  // Poll scanning status if attachment has file_key and it includes "quarantine"
  // Only poll if current status is not a final status (null, PENDING, or not CLEAN/INFECTED/ERROR)
  const initialStatus = attachment.scanned_status;
  const isQuarantineFile = attachment.file_key && attachment.file_key.includes('quarantine');
  const hasFinalStatus = initialStatus && ['CLEAN', 'INFECTED', 'ERROR'].includes(initialStatus);
  const shouldPoll = isQuarantineFile && !hasFinalStatus;
  
  const { scannedStatus, isScanningComplete, isPolling } = useScanningStatusSSE(
    shouldPoll ? attachment.file_key : null,
    shouldPoll
  );
  

  // Use polled status if available, otherwise use initial status from attachment
  const [displayStatus, setDisplayStatus] = useState(() => scannedStatus || initialStatus);
  const [hideStatus, setHideStatus] = useState(false);
  const cleanTimeoutRef = React.useRef(null);
  const cleanHandledRef = React.useRef(false); // Track if CLEAN timeout has been set
  const urlRefetchedRef = React.useRef(false); // Track if URL has been refetched for CLEAN status

  // Handle case where Clean status appears - show for 5 seconds then hide completely
  React.useEffect(() => {
    const currentStatus = scannedStatus || initialStatus;

    // If status is CLEAN and we haven't handled it yet, set timeout
    if (currentStatus === 'CLEAN' && !cleanHandledRef.current) {
      setDisplayStatus('CLEAN');
      setHideStatus(false);
      cleanHandledRef.current = true; // Mark as handled
      
      cleanTimeoutRef.current = setTimeout(() => {
        setDisplayStatus(null);
        setHideStatus(true); // Hide status completely after 5 seconds
      }, 5000);
    } else if (currentStatus === 'INFECTED' || currentStatus === 'ERROR') {
      // Show INFECTED or ERROR immediately and keep showing
      // Reset clean handled flag if status changes
      if (cleanHandledRef.current) {
        cleanHandledRef.current = false;
        if (cleanTimeoutRef.current) {
          clearTimeout(cleanTimeoutRef.current);
          cleanTimeoutRef.current = null;
        }
      }
      setDisplayStatus(currentStatus);
      setHideStatus(false);
    } else if (currentStatus !== 'CLEAN') {
      // For null or other statuses (not CLEAN), only show if we haven't hidden after CLEAN
      // Reset clean handled flag if status changes away from CLEAN
      if (cleanHandledRef.current && currentStatus !== 'CLEAN') {
        cleanHandledRef.current = false;
        if (cleanTimeoutRef.current) {
          clearTimeout(cleanTimeoutRef.current);
          cleanTimeoutRef.current = null;
        }
      }
      
      if (!hideStatus) {
        setDisplayStatus(currentStatus);
      } else {
        // If we've hidden after CLEAN, keep it hidden
        setDisplayStatus(null);
      }
    }

    // Cleanup timeout on unmount
    return () => {
      if (cleanTimeoutRef.current) {
        clearTimeout(cleanTimeoutRef.current);
        cleanTimeoutRef.current = null;
      }
    };
  }, [scannedStatus, initialStatus, hideStatus]);

  // Check if file is an image based on MIME type or file extension
  const isImage = () => {
    const mimeType = attachment.file_type;
    const fileName = attachment.file_name;
    
    // Check MIME type first
    if (mimeType?.startsWith('image/')) return true;
    
    // Fallback: check file extension when MIME type is generic
    if (mimeType === 'application/octet-stream' && fileName) {
      const fileExtension = fileName.split('.').pop()?.toLowerCase();
      return ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(fileExtension);
    }
    
    return false;
  };

  // Only request signed URLs after scan is CLEAN (or legacy permanent files with no status)
  const effectiveScanStatus = scannedStatus || initialStatus;
  const canAccessFile =
    effectiveScanStatus === 'CLEAN' ||
    (!isQuarantineFile && !effectiveScanStatus);

  // Fetch download URL only when file is safe to access
  useEffect(() => {
    if (attachment.file_key && canAccessFile) {
      fetchDownloadUrl();
    } else {
      setDownloadUrl(null);
    }
  }, [attachment.file_key, canAccessFile]);

  const fetchDownloadUrl = async (fileKey = null) => {
    const keyToUse = fileKey || attachment.file_key;
    if (!keyToUse || keyToUse.includes('quarantine')) return;
    
    setIsLoading(true);
    try {
      const response = await fetchAttachmentDownloadUrl(keyToUse);
      if (response?.downloadUrl) {
        setDownloadUrl(response.downloadUrl);
      }
    } catch (error) {
      console.error('Error fetching download URL:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Refetch download URL when file becomes CLEAN (moved from quarantine to permanent)
  useEffect(() => {
    if (scannedStatus === 'CLEAN' && isScanningComplete && isQuarantineFile && !urlRefetchedRef.current) {
      // Convert quarantine path to permanent path
      const pathParts = attachment.file_key.split('/');
      const quarantineIndex = pathParts.indexOf('quarantine');
      
      if (quarantineIndex !== -1) {
        pathParts.splice(quarantineIndex, 1); // Remove 'quarantine'
        pathParts.splice(quarantineIndex + 2, 0, 'attachments'); // Insert 'attachments'
        const permanentFileKey = pathParts.join('/');
        
        urlRefetchedRef.current = true; // Mark as refetched
        fetchDownloadUrl(permanentFileKey);
      }
    }
  }, [scannedStatus, isScanningComplete, isQuarantineFile, attachment.file_key]);

  const handleDownload = async () => {
    if (onDownload) {
      onDownload(attachment);
      return;
    }

    if (downloadUrl) {
      // Since backend now provides signed URL with Content-Disposition: attachment,
      // we can use simple methods that should work reliably
      
      try {
        // Method 1: Direct link click (should work with Content-Disposition header)
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = attachment.file_name || `attachment-${attachment.id}`;
        link.style.display = 'none';
        
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        console.log('Download initiated for original filename:', attachment.file_name);
        return;
      } catch (error) {
        console.warn('Direct download failed:', error);
      }

      try {
        // Method 2: Fetch and blob download (fallback)
        const response = await fetch(downloadUrl);
        if (response.ok) {
          const blob = await response.blob();
          const url = window.URL.createObjectURL(blob);
          
          const a = document.createElement('a');
          a.href = url;
          a.download = attachment.file_name || `attachment-${attachment.id}`;
          a.style.display = 'none';
          
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          
          // Cleanup
          setTimeout(() => window.URL.revokeObjectURL(url), 100);
          console.log('Blob download completed for:', attachment.file_name);
          return;
        }
      } catch (error) {
        console.warn('Blob download failed:', error);
      }

      // Method 3: Window.open as last resort
      console.warn('Using window.open as fallback for:', attachment.file_name);
      window.open(downloadUrl, '_blank');
    }
  };

  const handlePreview = () => {
    if (downloadUrl && isImage()) {
      setPreviewModalOpen(true);
    }
  };

  const handlePreviewClose = () => {
    setPreviewModalOpen(false);
  };

  const handleDelete = () => {
    setShowDeleteDialog(true);
  };

  const confirmDelete = () => {
    if (onDelete) {
      // Use custom delete handler if provided
      onDelete(attachment.id, attachment);
    } else {
      // Fall back to Redux dispatch
      dispatch(deleteAttachment(attachment.id));
    }
    setShowDeleteDialog(false);
  };

  const formatFileSize = (size) => {
    if (!size || size === 0 || isNaN(size)) return 'Unknown size';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(size) / Math.log(k));
    return parseFloat((size / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileExtension = (fileName) => {
    return fileName?.split('.').pop()?.toUpperCase() || 'FILE';
  };

  const getDisplayType = () => {
    console.log('attachment', attachment);
    const mimeType = attachment.file_type;
    const fileName = attachment.file_name;
    
    if (!mimeType) return 'FILE';
    
    // Check MIME type first
    if (mimeType.startsWith('image/')) return 'IMAGE';
    if (mimeType === 'application/pdf') return 'PDF';
    if (mimeType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' || 
        mimeType === 'application/msword') return 'DOC';
    if (mimeType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' || 
        mimeType === 'application/vnd.ms-excel' || 
        mimeType === 'text/csv') return 'EXCEL';
    if (mimeType === 'text/plain') return 'TEXT';
    
    // Fallback: detect from file extension when MIME type is generic
    if (mimeType === 'application/octet-stream' && fileName) {
      const fileExtension = fileName.split('.').pop()?.toLowerCase();
      switch (fileExtension) {
        case 'pdf':
          return 'PDF';
        case 'jpg':
        case 'jpeg':
        case 'png':
        case 'gif':
        case 'webp':
          return 'IMAGE';
        case 'doc':
        case 'docx':
          return 'DOC';
        case 'xls':
        case 'xlsx':
          return 'EXCEL';
        case 'txt':
          return 'TEXT';
        default:
          return 'FILE';
      }
    }
    
    return 'FILE';
  };

  const getFileIcon = () => {
    const displayType = getDisplayType();

    console.log('displayType', displayType);
    switch (displayType) {
      case 'IMAGE':
        return <PhotoIcon className="w-14 h-14 text-purple-500" />;
      case 'PDF':
        return <DocumentTextIcon className="w-14 h-14 text-red-500" />;
      case 'DOC':
        return <DocumentTextIcon className="w-14 h-14 text-blue-500" />;
      case 'EXCEL':
        return <TableCellsIcon className="w-14 h-14 text-green-500" />;
      case 'TEXT':
        return <DocumentTextIcon className="w-14 h-14 text-gray-500" />;
      default:
        return <DocumentIcon className="w-14 h-14 text-gray-400" />;
    }
  };

  const getChipColor = () => {
    const displayType = getDisplayType();
    
    console.log('displayType', displayType);
    switch (displayType) {
      case 'PDF':
        return { backgroundColor: '#fef2f2', color: '#dc2626' };
      case 'DOC':
        return { backgroundColor: '#eff6ff', color: '#2563eb' };
      case 'EXCEL':
        return { backgroundColor: '#f0fdf4', color: '#16a34a' };
      case 'TEXT':
        return { backgroundColor: '#f8fafc', color: '#64748b' };
      default:
        return { backgroundColor: '#e2e8f0', color: '#475569' };
    }
  };

  return (
    <>
      <Card
        sx={{
          width: 280, // Fixed width like LocalFileCard
          height: 280, // Fixed height like LocalFileCard
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
          },
          border: '1px solid #e2e8f0',
          borderRadius: 2,
        }}
      >
        {/* Action buttons - Same position as LocalFileCard */}
        <Box
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            zIndex: 1,
            display: 'flex',
            gap: 0.5,
          }}
        >
          {/* Preview button - only for images */}
          {isImage() && downloadUrl && canAccessFile && (
            <Tooltip title="Preview Image" arrow>
              <IconButton
                size="small"
                onClick={handlePreview}
                sx={{
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  color: '#6366f1',
                  '&:hover': {
                    backgroundColor: '#f0f9ff',
                    color: '#4f46e5',
                  },
                }}
              >
                <EyeIcon style={{ width: 16, height: 16 }} />
              </IconButton>
            </Tooltip>
          )}

          {
            canAccessFile ?
            <>
              {/* Download button */}
              <Tooltip title="Download File" arrow>
              <IconButton
                size="small"
                onClick={handleDownload}
                disabled={!downloadUrl}
                sx={{
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  color: '#059669',
                  '&:hover': {
                    backgroundColor: '#f0fdf4',
                    color: '#047857',
                  },
                  '&:disabled': {
                    color: '#9ca3af',
                    backgroundColor: 'rgba(255, 255, 255, 0.7)',
                  },
                }}
              >
                <ArrowDownTrayIcon style={{ width: 16, height: 16 }} />
              </IconButton>
              </Tooltip>
            </>: null
          }

          {/* Delete button */}
          {canEdit && (
            <Tooltip title="Delete Attachment" arrow>
              <IconButton
                size="small"
                onClick={handleDelete}
                sx={{
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  color: '#ef4444',
                  '&:hover': {
                    backgroundColor: '#fef2f2',
                    color: '#dc2626',
                  },
                }}
              >
                <TrashIcon style={{ width: 16, height: 16 }} />
              </IconButton>
            </Tooltip>
          )}

          {/* Custom actions */}
          {customActions.map((action, index) => (
            <Tooltip key={index} title={action.title || 'Action'} arrow>
              <IconButton
                size="small"
                onClick={() => action.onClick(attachment.id, attachment)}
                color={action.color || 'default'}
                sx={{
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  '&:hover': {
                    backgroundColor: '#f8fafc',
                  },
                }}
              >
                {action.icon}
              </IconButton>
            </Tooltip>
          ))}
        </Box>

        {/* Preview section - Fixed height like LocalFileCard */}
        <Box
          sx={{
            height: 200, // Keep preview area at 200px
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#f8fafc',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          {isLoading ? (
            <CircularProgress size={32} />
          ) : isImage() && downloadUrl && canAccessFile ? (
            // Image preview
            <CardMedia
              component="img"
              image={downloadUrl}
              alt={attachment.file_name}
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'cover', // Same as LocalFileCard
                cursor: 'pointer', // Indicate it's clickable
              }}
              onClick={handlePreview} // Allow clicking on image to preview
              onError={(e) => console.error(`❌ Image failed to load for ${attachment.id}:`, e)}
            />
          ) : (
            // Document icon with file type
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1,
              }}
            >
              {getFileIcon()}
              <Chip
                label={getFileExtension(attachment.file_name)}
                size="small"
                sx={{
                  ...getChipColor(),
                  fontWeight: 600,
                  fontSize: '0.75rem',
                }}
              />
            </Box>
          )}
        </Box>

        {/* File details section - Dynamic height to fill remaining space */}
        <CardContent
          sx={{
            flex: 1, // Takes up remaining space
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center', // Center content vertically
            p: 2,
            '&:last-child': { pb: 2 },
          }}
        >
          <Box>
            <Tooltip title={attachment.file_name || 'Unknown file'} arrow placement="top">
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  color: '#1e293b',
                  mb: 0.5,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  cursor: 'help',
                }}
              >
                {attachment.file_name || `Attachment ${attachment.id}`}
              </Typography>
            </Tooltip>

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
              <Typography
                variant="caption"
                sx={{
                  color: '#64748b',
                  display: 'block',
                }}
              >
                {formatFileSize(attachment.file_size)}
              </Typography>

              {/* Scanning Status Indicator - Beside file size (only for quarantine files) */}
              {attachment.file_key && attachment.file_key.includes('quarantine') && !hideStatus && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {/* Show "Scanning..." if status is null/undefined and we're polling and not complete */}
                  {(!["CLEAN", "INFECTED", "ERROR"].includes(displayStatus) && isPolling && !isScanningComplete) && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <CircularProgress size={12} sx={{ color: '#d97706' }} />
                      <Typography variant="caption" sx={{ fontSize: '0.65rem', color: '#d97706' }}>
                        Scanning...
                      </Typography>
                    </Box>
                  )}
                  {displayStatus === 'CLEAN' && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <CheckCircleIcon className="w-3 h-3" style={{ color: '#059669' }} />
                      <Typography variant="caption" sx={{ fontSize: '0.65rem', color: '#059669' }}>
                        Scan: Clean
                      </Typography>
                    </Box>
                  )}
                  {displayStatus === 'INFECTED' && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <ExclamationTriangleIcon className="w-3 h-3" style={{ color: '#dc2626' }} />
                      <Typography variant="caption" sx={{ fontSize: '0.65rem', color: '#dc2626' }}>
                        Scan: Infected
                      </Typography>
                    </Box>
                  )}
                  {displayStatus === 'ERROR' && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <ExclamationTriangleIcon className="w-3 h-3" style={{ color: '#dc2626' }} />
                      <Typography variant="caption" sx={{ fontSize: '0.65rem', color: '#dc2626' }}>
                        Scan: Error
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Image Preview Modal */}
      {isImage() && (
        <ImagePreviewModal
          open={previewModalOpen}
          onClose={handlePreviewClose}
          imageUrl={downloadUrl}
          fileName={attachment.file_name}
          fileSize={attachment.file_size}
          fileType={getDisplayType()}
        />
      )}

      {/* Delete Confirmation Modal */}
      {canEdit && (
        <ConfirmationModal
          open={showDeleteDialog}
          onClose={() => setShowDeleteDialog(false)}
          onConfirm={confirmDelete}
          title={getDeleteModalTitle()}
          message={getDeleteConfirmMessage('attachment', 1, false)}
        />
      )}
    </>
  );
};

export default AttachmentCard; 